/**
 * HR Bulk Employee Import API client.
 *
 * POST /api/v1/hr/employees/bulk-import
 *
 * dryRun: true  → validate rows, return preview with errors
 * dryRun: false → commit to database, audit-log each row
 *
 * The CSV uses HR's human-friendly headers (e.g. "First Name", "Birth Date",
 * "Department", "Manager"); any column order works (papaparse reads by header
 * name) and unknown columns (Nationality, Age, Company Name, Job Grade, Office)
 * are ignored. Legacy snake_case headers (email, team_name, birth_date, …) are
 * still accepted for backward compatibility.
 */

import Papa from 'papaparse'
import { z } from 'zod'
import { apiRequest } from './client'

// ── Zod schemas ───────────────────────────────────────────────────────────────

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export const BulkImportRowSchema = z.object({
  email: z.string().email('Invalid email address'),
  // Generic template: on an UPDATE only the columns present are written, so
  // these are optional here (an empty cell → the backend leaves it untouched).
  // A genuine new hire still needs them — the backend enforces that on create.
  name: z.string().optional(),
  teamName: z.string().optional(),
  jobTitle: z.string().optional(),
  // Single role or a delimited list (e.g. "hod;hr;manager"). Absent → roles
  // left untouched (no inference). Each token must be a known role.
  role: z
    .string()
    .optional()
    .refine(
      (v) =>
        v == null ||
        v
          .split(/[;,/]/)
          .every((t) =>
            ['employee', 'manager', 'hod', 'hr', 'director', 'exec_director'].includes(
              t.trim().toLowerCase(),
            ),
          ),
      {
        message:
          'role must be one of employee, manager, hod, hr, director, exec_director (a delimited list is allowed)',
      },
    ),
  internalId: z.string().optional(),
  birthDate: z.string().regex(ISO_DATE, 'Birth Date must be YYYY-MM-DD').optional(),
  gender: z.enum(['F', 'M', 'X']).optional(),
  reportsToEmail: z.string().email('reports_to_email must be a valid email').optional(),
  managerName: z.string().optional(),
  joinDate: z.string().regex(ISO_DATE, 'Joining Date must be YYYY-MM-DD').optional(),
  probationEndDate: z
    .string()
    .regex(ISO_DATE, 'Confirmation Date must be YYYY-MM-DD')
    .optional(),
  contractEndDate: z
    .string()
    .regex(ISO_DATE, 'Contract Due Date must be YYYY-MM-DD')
    .optional(),
  lastWorkingDay: z
    .string()
    .regex(ISO_DATE, 'Employment End Date must be YYYY-MM-DD')
    .optional(),
  race: z.enum(['chinese', 'indian', 'malay', 'other']).optional(),
  // Free text — any religion is stored verbatim (known values still drive
  // default observed holidays server-side; others are HR-managed).
  religion: z.string().optional(),
  employmentType: z
    .enum(['full_time', 'contractual'], {
      message: 'Employment Type must be Permanent/Full Time or Contract/Contractual',
    })
    .optional(),
  preferredName: z.string().optional(),
  married: z.boolean().optional(),
  children: z.number().int().nonnegative().optional(),
  hometown: z.string().optional(),
  distanceKm: z.number().int().nonnegative().optional(),
  onProbation: z.boolean().optional(),
  active: z.boolean().optional(),
  isManager: z.boolean().optional(),
  isHod: z.boolean().optional(),
})

export type BulkImportRow = z.infer<typeof BulkImportRowSchema>

export const RowResultSchema = z.object({
  rowIndex: z.number().int(),
  email: z.string(),
  action: z.enum(['create', 'update', 'skip', 'error']),
  error: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
})

export type RowResult = z.infer<typeof RowResultSchema>

export const BulkImportResultSchema = z.object({
  dryRun: z.boolean(),
  total: z.number().int(),
  wouldCreate: z.number().int(),
  wouldUpdate: z.number().int(),
  errorCount: z.number().int(),
  rows: z.array(RowResultSchema),
})

export type BulkImportResult = z.infer<typeof BulkImportResultSchema>

// ── Header mapping ──────────────────────────────────────────────────────────────

/**
 * Map a normalised (lowercased, trimmed) CSV header to a canonical field key.
 * Both HR's human headers and the legacy snake_case headers are accepted.
 * Headers not in this map (Nationality, Age, Company Name, Job Grade, Office, …)
 * are ignored.
 */
const HEADER_ALIASES: Readonly<Record<string, string>> = {
  // identity
  'first name': 'firstName',
  'last name': 'lastName',
  name: 'name',
  'full name': 'name',
  'preferred name': 'preferredName',
  preferred_name: 'preferredName',
  email: 'email',
  'email address': 'email',
  'internal id': 'internalId',
  internal_id: 'internalId',
  'staff id': 'internalId',
  'employee id': 'internalId',
  // demographics
  'birth date': 'birthDate',
  birth_date: 'birthDate',
  'date of birth': 'birthDate',
  gender: 'gender',
  'marital status': 'maritalStatus',
  marital_status: 'maritalStatus',
  married: 'married',
  race: 'race',
  religion: 'religion',
  hometown: 'hometown',
  children: 'children',
  distance_km: 'distanceKm',
  // job / placement
  'job title': 'jobTitle',
  job_title: 'jobTitle',
  'employment type': 'employmentType',
  employment_type: 'employmentType',
  // "Approver" is the canonical column; "Manager" kept as a legacy alias.
  approver: 'managerName',
  manager: 'managerName',
  'reports to email': 'reportsToEmail',
  reports_to_email: 'reportsToEmail',
  // "Team" is the canonical column; "Department" kept as a legacy alias.
  team: 'teamName',
  'team name': 'teamName',
  team_name: 'teamName',
  department: 'teamName',
  role: 'role',
  'lms role': 'role',
  on_probation: 'onProbation',
  is_manager: 'isManager',
  is_hod: 'isHod',
  // lifecycle
  'joining date': 'joinDate',
  'join date': 'joinDate',
  join_date: 'joinDate',
  'probation period': 'probationPeriod',
  probation_period: 'probationPeriod',
  'confirmation date': 'probationEndDate',
  probation_end_date: 'probationEndDate',
  'contract due date': 'contractEndDate',
  contract_end_date: 'contractEndDate',
  'employment end date': 'lastWorkingDay',
  last_working_day: 'lastWorkingDay',
  'active/inactive': 'activeStatus',
  status: 'activeStatus',
  active: 'active',
}

/**
 * Canonical fields that must be derivable from the header row. Birth Date and
 * Gender are NOT required *headers* — they're required per-row only for CREATE
 * rows, which the backend enforces (a create row missing them becomes an error
 * row in the preview), so a column-less file still imports updates fine.
 */
// The generic template only *requires* an Email column (the row key). Name /
// Job Title / Department are required per-row only for NEW hires — enforced by
// the backend, which knows create-vs-update; an update patches whatever's given.
const REQUIRED_FIELDS = ['email'] as const

/** Human-facing label for a missing required field (for the error message). */
const REQUIRED_LABEL: Readonly<Record<string, string>> = {
  email: 'Email',
  name: 'First Name (or Name)',
  jobTitle: 'Job Title',
  teamName: 'Department',
}

// ── Value coercion helpers ────────────────────────────────────────────────────

/** boolean from true/1/yes/y, false/0/no/n; empty → undefined; else raw (Zod rejects). */
function toBool(v: string | undefined): boolean | string | undefined {
  const t = (v ?? '').trim().toLowerCase()
  if (!t) return undefined
  if (t === 'true' || t === '1' || t === 'yes' || t === 'y') return true
  if (t === 'false' || t === '0' || t === 'no' || t === 'n') return false
  return t
}

/** non-negative integer; empty → undefined; else raw string (Zod rejects). */
function toInt(v: string | undefined): number | string | undefined {
  const t = (v ?? '').trim()
  if (!t) return undefined
  const n = Number(t)
  if (Number.isInteger(n) && n >= 0) return n
  return t
}

/** trim + lowercase a free-text religion cell. Empty → undefined. Stored verbatim. */
function toReligion(v: string | undefined): string | undefined {
  return (v ?? '').trim().toLowerCase() || undefined
}

/**
 * Some rosters put a religion (e.g. "Sikh") in the Race column. We capture it
 * as the person's religion and leave race BLANK — the importer doesn't guess
 * someone's ethnicity. HR sets the race + observed holidays deliberately
 * afterwards. Extend this set as other religion-in-race values surface.
 */
const RELIGION_IN_RACE_COLUMN: Readonly<Record<string, string>> = {
  sikh: 'sikh',
}

/**
 * Map a Race cell to the LMS race enum. Empty → undefined. A valid race passes
 * through; anything else is returned raw so Zod rejects it as a row error
 * (HR fixes it) — we never silently flatten an unknown race to "other".
 * Religion-in-race values (see RACE_AS_RELIGION) are handled before this.
 */
function toRace(v: string | undefined): string | undefined {
  const t = (v ?? '').trim().toLowerCase()
  if (!t) return undefined
  if (['chinese', 'indian', 'malay', 'other'].includes(t)) return t
  return t
}

/**
 * Normalise a date cell to ISO YYYY-MM-DD. Accepts ISO already, or
 * day-first D/M/YYYY (the Malaysian convention, e.g. "7/8/1983" → 1983-08-07);
 * also tolerates "-" or "." separators. Disambiguates: a first part > 12 is a
 * day (day-first); a second part > 12 is month-first (US) and gets swapped;
 * otherwise day-first is assumed. Empty → undefined; unparseable → the raw
 * string so Zod rejects it with a clear error.
 */
/**
 * Zero-pad the Employee ID to the 5-digit canonical form (3548 → 03548) so it
 * matches how the old leave export formats staff numbers. Already-5-digit or
 * longer numbers are unchanged; non-numeric values pass through untouched.
 */
function padEmployeeId(v: string | undefined): string | undefined {
  const s = (v ?? '').trim()
  if (s === '') return undefined
  const core = s.replace(/^0+/, '')
  if (core === '' || !/^\d+$/.test(core)) return s
  return core.padStart(5, '0')
}

function toIsoDate(v: string | undefined): string | undefined {
  const t = (v ?? '').trim()
  if (!t) return undefined
  if (ISO_DATE.test(t)) return t
  const m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/)
  if (!m) return t
  let day = Number(m[1])
  let month = Number(m[2])
  const year = Number(m[3])
  if (month > 12 && day <= 12) {
    // second part can't be a month → it's month-first (US); swap.
    ;[day, month] = [month, day]
  }
  if (day < 1 || day > 31 || month < 1 || month > 12) return t
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${year}-${pad(month)}-${pad(day)}`
}

/**
 * Normalise an LMS Role cell to the role enum, tolerating common spellings.
 * Empty → 'employee' (the default). An unrecognised non-empty value is passed
 * through (lowercased) so Zod rejects it → row error with a line number.
 */
function toRole(v: string | undefined): string {
  const t = (v ?? '').trim().toLowerCase()
  if (!t) return 'employee'
  if (['hod', 'head of department', 'head', 'department head'].includes(t)) return 'hod'
  if (['manager', 'mgr', 'team manager', 'line manager'].includes(t)) return 'manager'
  if (['exec_director', 'exec director', 'executive director', 'ed'].includes(t)) {
    return 'exec_director'
  }
  if (['director', 'dir'].includes(t)) return 'director'
  if (['hr', 'human resources'].includes(t)) return 'hr'
  if (['employee', 'staff', 'exec', 'executive', 'individual contributor', 'ic'].includes(t)) {
    return 'employee'
  }
  return t
}

/**
 * "Male"→M, "Female"→F, "Other"/"X"→X, M/F/X passthrough. Empty → undefined.
 * An unrecognised value is returned raw so Zod rejects it (a garbage gender
 * should surface as a row error for HR to fix, not be silently coerced).
 */
function toGender(v: string | undefined): string | undefined {
  const t = (v ?? '').trim().toLowerCase()
  if (!t) return undefined
  if (t === 'm' || t === 'male') return 'M'
  if (t === 'f' || t === 'female') return 'F'
  if (t === 'x' || t === 'other') return 'X'
  return (v ?? '').trim()
}

/** Marital Status → married bool. "married…"→true; any other non-empty→false; empty→undefined. */
function maritalToMarried(v: string | undefined): boolean | undefined {
  const t = (v ?? '').trim().toLowerCase()
  if (!t) return undefined
  return t.startsWith('married')
}

/** "Active"→true, "Inactive"→false; falls back to generic bool tokens. Empty → undefined. */
function activeStatusToBool(v: string | undefined): boolean | string | undefined {
  const t = (v ?? '').trim().toLowerCase()
  if (!t) return undefined
  if (t.startsWith('active')) return true
  if (t.startsWith('inactive')) return false
  return toBool(t)
}

/**
 * Probation Period → on_probation bool. Empty → undefined.
 * "no"/"none"/"0"/"false"/"confirmed"/"n/a" → false; any other non-empty
 * (e.g. "3 months", "Yes") → true.
 */
function probationToBool(v: string | undefined): boolean | undefined {
  const t = (v ?? '').trim().toLowerCase()
  if (!t) return undefined
  if (['no', 'none', '0', 'false', 'confirmed', 'n/a', 'na', '-'].includes(t)) return false
  return true
}

/** Map common Employment Type values to the LMS enum; unknown → raw (Zod rejects). */
function toEmploymentType(v: string | undefined): string | undefined {
  const t = (v ?? '').trim().toLowerCase()
  if (!t) return undefined
  if (['permanent', 'full time', 'full-time', 'fulltime', 'full_time'].includes(t)) {
    return 'full_time'
  }
  if (['contract', 'contractual', 'fixed term', 'fixed-term', 'temporary'].includes(t)) {
    return 'contractual'
  }
  return t
}

/**
 * Infer the LMS role from a Job Title when the CSV has no explicit Role column
 * (the official roster expresses seniority only via title). "Head of X" → hod;
 * chief/CEO/CTO/CFO/COO/"executive director" → exec_director; "director" →
 * director; "... manager" → manager; everything else → employee. Conservative
 * on purpose ("Lead"/"Senior" stay employee — HR can promote later).
 */

/**
 * Derive on_probation from a Confirmation Date (the date probation ended).
 * Past or today → confirmed (false); future → still on probation (true);
 * empty / unparseable → undefined (let the server default apply).
 */
function probationFromConfirmation(confirmationDate: string | undefined): boolean | undefined {
  const t = (confirmationDate ?? '').trim()
  if (!ISO_DATE.test(t)) return undefined
  const today = new Date().toISOString().slice(0, 10)
  return t > today // string compare is valid for ISO YYYY-MM-DD
}

// ── CSV parsing ───────────────────────────────────────────────────────────────

/**
 * Parse a CSV string into typed import rows. Columns may be in any order and
 * use HR's human headers or legacy snake_case; unknown columns are ignored.
 * Returns row-level errors with the source line number.
 */
export function parseCsvToRows(csv: string): {
  rows: BulkImportRow[]
  errors: Array<{ line: number; message: string }>
} {
  const parsed = Papa.parse<Record<string, string>>(csv, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim().toLowerCase(),
  })

  const fields = parsed.meta.fields ?? []
  if (parsed.data.length === 0 && fields.length === 0) {
    return { rows: [], errors: [] }
  }

  // Which canonical fields does the header row supply?
  const present = new Set<string>()
  for (const f of fields) {
    const canonical = HEADER_ALIASES[f]
    if (canonical) present.add(canonical)
  }
  const missing = REQUIRED_FIELDS.filter((field) => !present.has(field))
  if (missing.length > 0) {
    const labels = missing.map((f) => REQUIRED_LABEL[f] ?? f)
    return { rows: [], errors: [{ line: 1, message: `Missing columns: ${labels.join(', ')}` }] }
  }

  const rows: BulkImportRow[] = []
  const errors: Array<{ line: number; message: string }> = []

  parsed.data.forEach((raw, i) => {
    // Re-key the row by canonical field name (ignoring unmapped columns).
    const f: Record<string, string> = {}
    for (const [rawKey, value] of Object.entries(raw)) {
      const canonical = HEADER_ALIASES[rawKey]
      if (canonical) f[canonical] = (value ?? '').trim()
    }

    const name =
      f['name'] || [f['firstName'], f['lastName']].filter(Boolean).join(' ').trim()

    // Race / religion. If the Race cell holds a religion (e.g. "Sikh"), capture
    // it as religion and leave race blank (no ethnicity guessing); else read
    // each column. An explicit Religion column always wins.
    const rawRaceLower = (f['race'] ?? '').trim().toLowerCase()
    const explicitReligion = toReligion(f['religion'])
    const religionInRace = RELIGION_IN_RACE_COLUMN[rawRaceLower]
    const race = religionInRace ? undefined : toRace(f['race'])
    const religion = explicitReligion ?? religionInRace

    // Normalise all date cells to ISO up front (accepts day-first D/M/YYYY).
    const birthDate = toIsoDate(f['birthDate'])
    const joinDate = toIsoDate(f['joinDate'])
    const confirmationDate = toIsoDate(f['probationEndDate'])
    const contractEndDate = toIsoDate(f['contractEndDate'])
    const lastWorkingDay = toIsoDate(f['lastWorkingDay'])

    const candidate: Record<string, unknown> = {
      email: f['email'] ?? '',
      name,
      teamName: f['teamName'] ?? '',
      jobTitle: f['jobTitle'] ?? '',
      // Only send Role when the column carries a value — never inferred from
      // the job title. Normalise each token (friendly labels → canonical) and
      // keep it as a delimited list; the backend splits + validates. Absent →
      // omitted → the backend leaves the employee's roles untouched.
      role: f['role']
        ? f['role']
            .split(/[;,/]/)
            .map((r) => toRole(r))
            .filter(Boolean)
            .join(';')
        : undefined,
      internalId: padEmployeeId(f['internalId']),
      birthDate,
      gender: toGender(f['gender']),
      reportsToEmail: f['reportsToEmail'] || undefined,
      managerName: f['managerName'] || undefined,
      joinDate,
      probationEndDate: confirmationDate,
      contractEndDate,
      lastWorkingDay,
      race,
      religion,
      employmentType: toEmploymentType(f['employmentType']),
      preferredName: f['preferredName'] || undefined,
      // married: prefer "Marital Status", fall back to a legacy "married" bool column
      married:
        f['maritalStatus'] !== undefined ? maritalToMarried(f['maritalStatus']) : toBool(f['married']),
      children: toInt(f['children']),
      hometown: f['hometown'] || undefined,
      distanceKm: toInt(f['distanceKm']),
      // on_probation precedence: explicit Probation Period → legacy on_probation
      // bool → derive from Confirmation Date (past = confirmed) → server default.
      onProbation:
        f['probationPeriod'] !== undefined
          ? probationToBool(f['probationPeriod'])
          : f['onProbation'] !== undefined
            ? toBool(f['onProbation'])
            : probationFromConfirmation(confirmationDate),
      // active: prefer "Active/Inactive", fall back to a legacy bool column
      active:
        f['activeStatus'] !== undefined ? activeStatusToBool(f['activeStatus']) : toBool(f['active']),
      isManager: toBool(f['isManager']),
      isHod: toBool(f['isHod']),
    }

    const result = BulkImportRowSchema.safeParse(candidate)
    if (!result.success) {
      errors.push({
        line: i + 2,
        message: result.error.issues.map((e) => e.message).join('; '),
      })
    } else {
      rows.push(result.data)
    }
  })

  return { rows, errors }
}

// ── API function ──────────────────────────────────────────────────────────────

export async function bulkImportEmployees(
  rows: BulkImportRow[],
  dryRun: boolean,
): Promise<BulkImportResult> {
  const raw = await apiRequest<unknown>('/api/v1/hr/employees/bulk-import', {
    method: 'POST',
    body: { rows, dryRun },
  })
  return BulkImportResultSchema.parse(raw)
}
